// Front-end mirror of api/src/lib/features.ts.
//
// Feature flags are product-level switches (per client/deployment), distinct
// from user-level permissions. A module is shown only when its feature flag is
// ON *and* the user has the relevant permission. When the flag is OFF the
// module is hidden everywhere regardless of permissions.

export interface FeatureFlags {
  production_orders: boolean;
  tax: boolean;
}

export type FeatureName = keyof FeatureFlags;

// Default to ON so a brief load (or a fetch failure) never hides a module the
// client actually has. The API is the authoritative gate (routes 404 when a
// flag is off), so an optimistic default here is safe.
export const DEFAULT_FEATURES: FeatureFlags = {
  production_orders: true,
  tax: true,
};

function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes" || v === "on") return true;
    if (v === "false" || v === "0" || v === "no" || v === "off") return false;
  }
  return fallback;
}

// Coerce an arbitrary API payload into a full FeatureFlags object.
export function normalizeFeatures(raw: unknown): FeatureFlags {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    production_orders: asBool(
      obj.production_orders,
      DEFAULT_FEATURES.production_orders,
    ),
    tax: asBool(obj.tax, DEFAULT_FEATURES.tax),
  };
}
